import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import type { ArticleData } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://pandabuy.cv";

function buildArticleJsonLd(article: ArticleData) {
  const pageUrl = `${siteUrl}/article/${article.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.metaDescription,
    author: {
      "@type": "Organization",
      name: "PandabuySpreadsheet",
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "PandabuySpreadsheet",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg",
      },
    },
    datePublished: "2026-06-01",
    dateModified: "2026-07-01",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl,
    },
    image: article.headerImage,
  };
}

function NotFoundArticle() {
  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center">
      <div className="text-center px-6">
        <h1 className="text-4xl font-heading font-bold text-foreground-950">Article Not Found</h1>
        <p className="mt-4 text-foreground-500">The article you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-accent-500 text-white text-sm font-semibold rounded-full hover:bg-accent-600 transition-all cursor-pointer whitespace-nowrap">
          Back to Homepage
          <i className="ri-arrow-left-line"></i>
        </Link>
      </div>
    </div>
  );
}

function ArticleContent({ article }: { article: ArticleData }) {
  return (
    <div className="prose-custom space-y-10">
      {article.sections.map((section, idx) => {
        if (section.type === "heading") {
          return (
            <h2 key={idx} className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
              {section.heading}
            </h2>
          );
        }

        if (section.type === "text") {
          return (
            <p key={idx} className="text-base text-foreground-600 leading-relaxed">
              {section.content}
            </p>
          );
        }

        if (section.type === "list") {
          return (
            <div key={idx}>
              {section.heading && (
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">{section.heading}</h3>
              )}
              <ul className="space-y-2.5">
                {section.items?.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-foreground-600 leading-relaxed">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        if (section.type === "cta") {
          return (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-2 rounded-3xl overflow-hidden border border-background-200">
              <div className="relative h-[220px] md:h-auto overflow-hidden">
                <img
                  src={section.image}
                  alt={section.imageAlt || "Visit Main Store"}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <span className="text-xs text-white/70 uppercase tracking-wider">Shop Now</span>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center px-8 py-12 bg-white text-center">
                <h3 className="text-2xl font-heading font-bold text-foreground-950">Ready to Start Shopping?</h3>
                <p className="mt-3 text-sm text-foreground-500 leading-relaxed max-w-xs">
                  Browse the full collection of curated spreadsheets on our main store. Updated weekly with fresh finds.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 mt-6 px-7 py-3 bg-foreground-900 text-white text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap group"
                >
                  Visit Main Store
                  <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
                </a>
              </div>
            </div>
          );
        }

        return null;
      })}

      <section className="pt-6 border-t border-background-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-accent-500 text-white text-sm font-semibold rounded-full hover:bg-accent-600 transition-all cursor-pointer whitespace-nowrap group"
          >
            Back to Homepage
            <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform"></i>
          </Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-foreground-900 text-white text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap group"
          >
            Pandabuy Sheet
            <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
          </a>
        </div>
      </section>
    </div>
  );
}

export default function GenericArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFoundArticle />;
  }

  return (
    <div className="min-h-screen bg-background-50 font-sans">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildArticleJsonLd(article)) }}
      />
      <Navbar />

      <main>
        <section className="relative w-full min-h-[380px] md:min-h-[480px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.headerImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/35 to-black/20"></div>
          </div>

          <div className="relative z-10 w-full px-6 md:px-10 pt-20">
            <div className="max-w-3xl">
              <nav className="flex items-center gap-2 text-sm text-white/60 mb-4">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-white/80">{article.title}</span>
              </nav>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight">
                {article.title}
              </h1>
              <p className="mt-3 text-sm md:text-base text-white/60 leading-relaxed max-w-2xl">
                {article.subtitle}
              </p>
              <div className="mt-4 flex items-center gap-4 text-sm text-white/50">
                <span>Updated June 2026</span>
                <span>&middot;</span>
                <span>{article.readTime}</span>
                <span>&middot;</span>
                <span>{article.wordCount}</span>
              </div>
            </div>
          </div>
        </section>

        <article className="py-16 md:py-24">
          <div className="w-full px-6 md:px-10 max-w-4xl mx-auto">
            <ArticleContent article={article} />
          </div>
        </article>

        <section className="py-16 bg-background-100">
          <div className="w-full px-6 md:px-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">Explore More Categories</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border border-background-200 hover:border-accent-200 hover:bg-accent-50/30 transition-all cursor-pointer group"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 group-hover:bg-accent-100 transition-colors">
                    <i className={`${cat.icon} text-lg`}></i>
                  </span>
                  <span className="text-xs font-medium text-foreground-700 text-center whitespace-nowrap group-hover:text-foreground-950 transition-colors">
                    {cat.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}